import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsAuth from 'sdk/hooks/useIsAuth';
import useGetOrdersInTheCart from 'sdk/hooks/shop/useGetOrdersInTheCart';
import { ReactComponent as BackIcon } from 'images/new-icons/arrow-black-back.svg';
import { ReactComponent as CartIcon } from 'images/dark-green-cart.svg';
import s from './ShopTopMenu.module.scss';

interface ShopTopMenuProps {
  // Required props
  title: string;
  onCartClick: (value: boolean) => void;

  // Optional props with defaults
  backNavigationPath?: string;
  showCartCounter?: boolean;
  className?: string;
}

const ShopTopMenu: React.FC<ShopTopMenuProps> = ({
  title,
  onCartClick,
  backNavigationPath,
  showCartCounter = true,
  className,
}) => {
  const navigate = useNavigate();
  const { data: isAuthData } = useIsAuth();
  const userId = isAuthData?.user?.id;
  const { data: cartItems, refetch: refetchCart } = useGetOrdersInTheCart(userId);

  useEffect(() => {
    if (userId) {
      refetchCart();
    }
  }, [userId, refetchCart]);

  interface CartItem {
    count: number;
    price?: number;
    product?: {
      price?: number;
    };
  }
  const itemCount = (cartItems as CartItem[] | undefined)?.reduce((total: number, item: CartItem) => total + (item.count || 0), 0) || 0;
  const displayCount = itemCount > 9 ? '9+' : itemCount.toString();
  const showCounter = showCartCounter && itemCount > 0;

  const handleBackClick = () => {
    if (backNavigationPath) {
      navigate(backNavigationPath);
    } else {
      navigate(-1);
    }
  };

  const handleCartClick = () => {
    onCartClick(false);
  };

  const containerClasses = `${s.container} ${className || ''}`.trim();

  return (
    <div className={containerClasses}>
      <div 
        className={s.backButton} 
        onClick={handleBackClick}
        role="button"
        tabIndex={0}
        aria-label="Go back"
        onKeyDown={(e) => e.key === 'Enter' && handleBackClick()}
      >
        <BackIcon />
      </div>
      
      <div className={s.title}>{title}</div>
      
      <div className={s.rightIcons}>
        <div
          className={s.cartWrapper}
          onClick={handleCartClick}
          role="button"
          tabIndex={0}
          aria-label={`Shopping cart${itemCount > 0 ? ` with ${itemCount} items` : ''}`}
          onKeyDown={(e) => e.key === 'Enter' && handleCartClick()}
        >
          <CartIcon />
          {showCounter && <div className={s.counter}>{displayCount}</div>}
        </div>
      </div>
    </div>
  );
};

export default ShopTopMenu;
